import { Link, useLocation } from "@tanstack/react-router";
import { MASTER_DATA_ENTITY_TYPE_OPTIONS } from "@/lib/enums";
import { cn } from "@/lib/utils";

const BASE_PATH = "/dashboard/master-data-mappings";

/**
 * Route-driven tab bar, one per master-data entity type — mirrors
 * `SettingsLayout.tsx`'s pattern (a `<Link>` per tab, active-state via
 * `useLocation`) rather than introducing a new Tabs UI primitive. Every tab
 * renders the same `MasterDataMappingsTable`, just scoped to a different
 * `entityType` via the `/$entityType` dynamic route (see `router.tsx`).
 */
export function MasterDataMappingsLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold">Master Data Mappings</h1>
        <p className="mt-1 text-muted-foreground">
          Resolve a Coach Manager master-data reference (Operator, Base, Vehicle Type, ...) from its
          source-BU value to the equivalent target-BU value, used when previewing and copying
          records between Business Units.
        </p>
      </div>

      <div className="relative mb-8">
        <div className="-mx-4 px-4 overflow-x-auto md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <nav className="flex gap-1 whitespace-nowrap">
            {MASTER_DATA_ENTITY_TYPE_OPTIONS.map((tab) => {
              const href = `${BASE_PATH}/${tab.value}`;
              const isActive = location.pathname === href;

              return (
                <Link
                  key={tab.value}
                  to={href}
                  role="tab"
                  aria-selected={isActive}
                  className={cn(
                    "group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors rounded-md",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/80",
                  )}
                >
                  <span>{tab.label}</span>

                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
      </div>

      {children}
    </>
  );
}
